import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const FormCard = ({ form, index }) => {
  const navigate = useNavigate();
  return (
    <div direction="up" delay={index * 0.05}>
      <div onClick={() => navigate(`/forms/${form.code}`)} className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md p-4 cursor-pointer transition-all">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900">{form.name}</h3>
            <p className="text-xs text-gray-500 mt-1">Code: {form.code}</p>
            {form.price !== "0.00" && <p className="text-xs font-semibold text-green-600 mt-1">${form.price}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function FormsHero() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [forms, setForms] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");

  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSub, setLoadingSub] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const res = await api.get("/form-categories");
        if (res.data.success) {
          setCategories(res.data.data);
        }
      } catch (err) {
        Swal.fire("Error", "Failed to load categories.", "error");
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!selectedCategory) {
      setSubcategories([]);
      setSelectedSubcategory("");
      setForms([]);
      return;
    }

    const fetchSubcategories = async () => {
      try {
        setLoadingSub(true);
        const res = await api.get(`/form-categories/${selectedCategory}/subcategories`);
        if (res.data.success) {
          setSubcategories(res.data.data);
        }
      } catch (err) {
        console.error(err);
        setSubcategories([]);
      } finally {
        setLoadingSub(false);
      }
    };

    fetchSubcategories();
    setSelectedSubcategory("");
  }, [selectedCategory]);

  useEffect(() => {
    if (!selectedCategory || !selectedSubcategory) {
      setForms([]);
      return;
    }

    const fetchForms = async () => {
      try {
        setLoadingForms(true);

        const selectedSub = subcategories.find(s => s.id === parseInt(selectedSubcategory));
        if (!selectedSub) {
          setForms([]);
          return;
        }

        const res = await api.get("/forms", {
          params: {
            form_subcategory_slug: selectedSub.slug
          }
        });

        if (res.data.success) {
          setForms(res.data.data);
        } else {
          setForms([]);
        }
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to load forms.", "error");
        setForms([]);
      } finally {
        setLoadingForms(false);
      }
    };

    fetchForms();
  }, [selectedCategory, selectedSubcategory, subcategories]);

  return (
    <>
      <section className="relative isolate overflow-hidden bg-gray-50 py-24 md:py-32">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50 opacity-70" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-8 rounded-full border border-blue-200 bg-blue-50/80 px-6 py-2.5 backdrop-blur-sm">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-[pingSlow_2s_linear_infinite]" />
            <span className="text-sm font-semibold tracking-wide text-blue-800">
              {t("forms.hero.badge")}
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-black tracking-tight leading-tight text-blue-950 drop-shadow-sm">
            {t("forms.hero.title")}
          </h1>

          <div className="mx-auto mt-6 h-1 w-48 rounded-full bg-gradient-to-r from-blue-400 via-blue-600 to-indigo-600 shadow-lg shadow-blue-500/20" />

          <p className="mt-8 text-lg md:text-xl leading-relaxed text-gray-700 max-w-3xl mx-auto">
            {t("forms.hero.description")}
          </p>

          <div className="mt-14 max-w-2xl mx-auto space-y-7">
            <div className="relative">
              <label className="block text-sm font-semibold text-blue-900 mb-3 text-left">
                {t("forms.hero.search_label")}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={loadingCategories}
                className="w-full px-6 py-5 rounded-2xl bg-white border-2 border-blue-200 text-gray-900 text-lg font-medium
                     focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100
                     transition-all cursor-pointer shadow-lg shadow-blue-100/50 disabled:opacity-70"
              // style={{
              //   backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
              //   backgroundPosition: "right 1.5rem center",
              //   backgroundRepeat: "no-repeat",
              //   backgroundSize: "12px",
              // }}
              >
                <option value="">{loadingCategories ? t("forms.hero.loading_categories") : t("forms.hero.select_category_placeholder")}</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative">
              <label className="block text-sm font-semibold text-blue-900 mb-3 text-left">
                {t("forms.hero.subcategory_label")}
              </label>
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                disabled={!selectedCategory || loadingSub}
                className={`w-full px-6 py-5 rounded-2xl text-lg font-medium transition-all shadow-lg
                  ${selectedCategory
                    ? 'bg-white border-2 border-blue-200 text-gray-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 cursor-pointer'
                    : 'bg-gray-50 border-2 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                  }`}
                style={{
                  backgroundImage: selectedCategory ? `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")` : 'none',
                  backgroundPosition: "right 1.5rem center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "12px",
                }}
              >
                <option value="">
                  {loadingSub ? t("forms.hero.loading_subcategories") : selectedCategory ? t("forms.hero.all_in_category") : t("forms.hero.first_select_category")}
                </option>
                {subcategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Grid Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {loadingForms ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
              <p className="mt-4 text-lg text-gray-600">{t("forms.hero.loading_forms")}</p>
            </div>
          ) : forms.length > 0 ? (
            <>
              <h2 className="text-3xl font-bold text-gray-900 mb-12">
                {t("forms.hero.available_forms", { count: forms.length })}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {forms.map((form, index) => (
                  <FormCard key={form.id} form={form} index={index} />
                ))}
              </div>
            </>
          ) : selectedCategory ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">{t("forms.hero.no_forms_selection")}</p>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-2xl text-gray-500">{t("forms.hero.select_category_prompt")}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}